// src/components/Organograma.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Select from 'react-select'; 
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  MarkerType,
  Handle,
  Position,
  useReactFlow,
  ReactFlowProvider 
} from 'reactflow';
import dagre from 'dagre'; 
import 'reactflow/dist/style.css';

import { auth, db } from '../firebaseConfig';
import {
  collection, getDocs, doc, updateDoc, addDoc, getDoc, deleteDoc,
  query, where, writeBatch, serverTimestamp
} from 'firebase/firestore';

import '../styles/Organograma.css';

// --- CONFIGURAÇÃO VISUAL ---
const NODE_WIDTH = 280; 
const NODE_HEIGHT = 190; 

// --- ESTILOS DO SELECT (WHITE-LABEL) ---
// Agora usa as variáveis CSS do sistema para ficar igual ao resto
const customSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        minHeight: '38px',
        borderRadius: '6px',
        fontSize: '0.9rem',
        minWidth: '200px',
        backgroundColor: '#fff',
        // Usa a cor primária do tema se focado, senão borda padrão
        borderColor: state.isFocused ? 'var(--primary-color)' : '#dee2e6',
        boxShadow: state.isFocused ? '0 0 0 1px var(--primary-color)' : 'none',
        '&:hover': {
            borderColor: 'var(--primary-color)'
        }
    }),
    option: (provided, state) => ({
        ...provided,
        // Fundo da opção selecionada ou focada
        backgroundColor: state.isSelected ? 'var(--primary-color)' : state.isFocused ? 'var(--bg-light)' : '#fff',
        color: state.isSelected ? '#fff' : '#333',
        cursor: 'pointer',
        fontSize: '0.9rem'
    }),
    menu: (provided) => ({ ...provided, zIndex: 9999 }),
    placeholder: (provided) => ({ ...provided, color: '#999' })
};

// --- ALGORITMO DE LAYOUT ---
const getLayoutedElements = (nodes, edges, direction = 'TB') => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  dagreGraph.setGraph({ 
      rankdir: direction, 
      ranksep: 100, 
      nodesep: 50   
  });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      targetPosition: Position.Top,
      sourcePosition: Position.Bottom,
      position: { 
        x: nodeWithPosition.x - NODE_WIDTH / 2, 
        y: nodeWithPosition.y - NODE_HEIGHT / 2 
      },
    };
  });

  return { nodes: layoutedNodes, edges };
};

// --- COMPONENTE DO NÓ (CARD) ---
const UserNode = ({ data, isConnectable, selected }) => {
  const [imgError, setImgError] = useState(false);
  const topBorderColor = selected ? 'var(--primary-color)' : (data.isRoot ? 'var(--info-color)' : 'var(--border-color)');

  return (
    <div className={`org-node-card-manual ${data.isReadOnly ? 'read-only' : ''}`} style={{ borderColor: selected ? 'var(--primary-color)' : 'white' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: topBorderColor }}></div>
      
      {/* Esconde conectores se for apenas visualização */}
      {!data.isReadOnly && (
          <>
            <Handle type="target" position={Position.Top} isConnectable={isConnectable} className="org-handle" />
            <Handle type="source" position={Position.Bottom} isConnectable={isConnectable} className="org-handle" />
          </>
      )}

      <div className="org-card-header">
        {(data.photoURL && !imgError) ? (
          <img src={data.photoURL} alt="Foto" className="org-card-photo" onError={() => setImgError(true)} />
        ) : (
          <div className="org-card-photo-placeholder">{data.label ? data.label.charAt(0) : '?'}</div>
        )}
        <div className="org-card-info">
          <div className="org-card-name" title={data.label}>{data.label}</div>
          <div className="org-card-role" title={data.funcao}>{data.funcao || 'Posição Vaga'}</div>
        </div>
      </div>

      <div className="org-card-body">
         {data.loja && <div className="org-info-row"><span>Loja</span> <strong title={data.loja}>{data.loja}</strong></div>}
         {data.age && <div className="org-info-row"><span>Idade</span> <strong>{data.age} anos</strong></div>}
         
         {/* Botão de Olho (Permissão) */}
         {data.userId && (
             <div className="permission-toggle-row">
                 <span className="permission-label">Ver Equipe</span>
                 <button 
                    className={`permission-btn ${data.canViewSubordinates ? 'active' : 'inactive'} ${data.isReadOnly ? 'disabled' : ''}`}
                    onClick={(e) => { 
                        // Bloqueio extra no clique
                        if (!data.isReadOnly) {
                            e.stopPropagation(); 
                            data.onToggleVisibility(data.userId, !data.canViewSubordinates); 
                        }
                    }}
                    disabled={data.isReadOnly}
                    style={{ cursor: data.isReadOnly ? 'not-allowed' : 'pointer', opacity: data.isReadOnly ? 0.5 : 1 }}
                    title={data.isReadOnly ? "Apenas People pode alterar" : (data.canViewSubordinates ? "Acesso permitido" : "Acesso negado")}
                 >
                    {data.canViewSubordinates ? '👁️' : '👁️‍🗨️'}
                 </button>
             </div>
         )}
      </div>

      {/* Botões de Ação (Só aparecem se NÃO for ReadOnly) */}
      {!data.isReadOnly && (
        <div className="org-card-actions">
          {data.userId ? (
            <button onClick={(e) => {e.stopPropagation(); data.onRemoveUserFromNode(data.reactFlowId, data.userId)}} className="org-btn-icon remove" title="Desvincular">✕</button>
          ) : (
            <button onClick={(e) => {e.stopPropagation(); data.onAssignUserToNode(data.reactFlowId)}} className="org-btn-icon assign" title="Atribuir">👤+</button>
          )}
          <button onClick={(e) => {e.stopPropagation(); data.onEditNode(data.reactFlowId)}} className="org-btn-icon edit" title="Editar">✎</button>
          <button onClick={(e) => {e.stopPropagation(); data.onDeleteNode(data.reactFlowId)}} className="org-btn-icon delete" title="Excluir">🗑</button>
        </div>
      )}
    </div>
  );
};

const nodeTypes = { userNode: UserNode };

// --- LÓGICA PRINCIPAL ---
const OrganogramaContent = () => {
    // States do React Flow
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    
    // Dados
    const [rawUsers, setRawUsers] = useState([]);
    const [rawNodesData, setRawNodesData] = useState([]);
    
    // Filtros
    const [filterStore, setFilterStore] = useState(null);
    const [filterDept, setFilterDept] = useState(null);
    
    // Admin & Permissões
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [adminEmpresaId, setAdminEmpresaId] = useState(null);
    const [adminUserData, setAdminUserData] = useState(null);
    
    // Modais
    const [showAddNodeModal, setShowAddNodeModal] = useState(false);
    const [newNodeName, setNewNodeName] = useState("");
    const [addNodeParentId, setAddNodeParentId] = useState(null);
    const [showUserAssignmentModal, setShowUserAssignmentModal] = useState(false);
    const [selectedNodeForAssignment, setSelectedNodeForAssignment] = useState(null);
    const [userToAssignId, setUserToAssignId] = useState('');
    const [editingNodeData, setEditingNodeData] = useState(null);
    const [showEditNodeModal, setShowEditNodeModal] = useState(false);
    const [editNodeName, setEditNodeName] = useState("");

    const reactFlowInstance = useReactFlow();

    // ----------------------------------------------------------------------
    // 1. CÁLCULO DE PERMISSÃO (SEGURANÇA REFORÇADA)
    // ----------------------------------------------------------------------
    const isReadOnly = useMemo(() => {
        // Se ainda não carregou o usuário, bloqueia tudo por segurança
        if (!adminUserData) return true;
        
        const dept = (adminUserData.departamento || '').toLowerCase().trim();
        const role = (adminUserData.role || '').toLowerCase().trim();
        
        // Permite apenas se for do departamento 'people' OU se for 'admin'
        const isPeople = dept === 'people';
        //const isAdmin = role === 'admin';
        
        return !(isPeople);
    }, [adminUserData]);

    // 2. AUTH E CARREGAMENTO
    useEffect(() => {
      const unsubscribe = auth.onAuthStateChanged(async (user) => {
        if (user) {
            try {
                const uDoc = await getDoc(doc(db, 'users', user.uid));
                if (uDoc.exists()) {
                    const data = uDoc.data();
                    if(data.status === 'inactive') {
                        setError("Usuário inativo."); return;
                    }
                    setAdminUserData(data);
                    setAdminEmpresaId(data.empresa_id);
                }
            } catch(e) { console.error(e); setError("Erro de conexão"); }
        } else {
            setError("Não autenticado.");
        }
      });
      return () => unsubscribe();
    }, []);

    const fetchFromFirebase = useCallback(async () => {
        if (!adminEmpresaId) return;
        setLoading(true);
        try {
            const usersRef = collection(db, 'users');
            const qUsers = query(usersRef, where("empresa_id", "==", adminEmpresaId));
            const usersSnap = await getDocs(qUsers);
            
            const usersData = await Promise.all(usersSnap.docs.map(async (d) => {
                const pub = d.data();
                if (pub.status === 'inactive') return null;
                let age = null;
                try {
                   const priv = await getDoc(doc(db, 'usersprivado', d.id));
                   if (priv.exists() && priv.data().dataNascimento) {
                       const birth = priv.data().dataNascimento.toDate ? priv.data().dataNascimento.toDate() : new Date(priv.data().dataNascimento);
                       const diff = Date.now() - birth.getTime();
                       age = Math.abs(new Date(diff).getUTCFullYear() - 1970);
                   }
                } catch(e) {}
                return { id: d.id, ...pub, age };
            }));
            const validUsers = usersData.filter(u => u !== null);
            setRawUsers(validUsers);

            const nodesRef = collection(db, 'Empresas', adminEmpresaId, 'organizationChartNodes');
            const nodesSnap = await getDocs(nodesRef);
            const nodesList = nodesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setRawNodesData(nodesList);

        } catch (err) {
            console.error(err);
            setError("Erro ao carregar dados.");
        } finally {
            setLoading(false);
        }
    }, [adminEmpresaId]);

    useEffect(() => { fetchFromFirebase(); }, [fetchFromFirebase]);

    // 3. RENDERIZAÇÃO DO GRAFO
    useEffect(() => {
        if (rawNodesData.length === 0) return;

        const usersMap = {};
        rawUsers.forEach(u => usersMap[u.id] = u);

        // Filtra nós se houver filtro ativo
        let filteredNodesData = rawNodesData;
        if (filterStore || filterDept) {
            filteredNodesData = rawNodesData.filter(node => {
                if (!node.userId) return false; // Esconde vagos ao filtrar
                const user = usersMap[node.userId];
                if (!user) return false;
                const matchStore = !filterStore || user.loja === filterStore.value;
                const matchDept = !filterDept || user.departamento === filterDept.value;
                return matchStore && matchDept;
            });
        }

        const flowNodes = filteredNodesData.map(nodeData => {
            const assignedUser = nodeData.userId ? usersMap[nodeData.userId] : null;
            return {
                id: nodeData.id,
                type: 'userNode',
                // Se filtrado, reseta posição para auto-layout
                position: (filterStore || filterDept) ? { x: 0, y: 0 } : (nodeData.position || { x: 0, y: 0 }),
                draggable: !isReadOnly && !filterStore && !filterDept, // Só pode arrastar se não tiver filtro e tiver permissão
                selectable: !isReadOnly,
                data: {
                    label: assignedUser ? assignedUser.name : (nodeData.name || 'Vago'),
                    funcao: assignedUser ? assignedUser.funcao : 'Posição Vaga',
                    photoURL: assignedUser?.photoURL,
                    loja: assignedUser?.loja,
                    age: assignedUser?.age,
                    canViewSubordinates: assignedUser?.canViewSubordinates,
                    userId: nodeData.userId,
                    parentId: nodeData.parentId,
                    isRoot: !nodeData.parentId,
                    isReadOnly, // Flag crítica de permissão
                    reactFlowId: nodeData.id,
                    onRemoveUserFromNode: handleRemoveUserFromNode,
                    onAssignUserToNode: (id) => { setSelectedNodeForAssignment(id); setShowUserAssignmentModal(true); },
                    onEditNode: handleOpenEditModal,
                    onDeleteNode: handleDeleteNode,
                    onToggleVisibility: handleToggleManagerView
                }
            };
        });

        const flowEdges = flowNodes
            .filter(n => n.data.parentId && flowNodes.find(p => p.id === n.data.parentId))
            .map(n => ({
                id: `e-${n.data.parentId}-${n.id}`,
                source: n.data.parentId,
                target: n.id,
                type: 'smoothstep',
                style: { stroke: '#b1b1b7', strokeWidth: 2 },
                markerEnd: { type: MarkerType.ArrowClosed, color: '#b1b1b7' }
            }));

        const shouldLayout = (filterStore || filterDept) || flowNodes.every(n => n.position.x === 0 && n.position.y === 0);

        if (shouldLayout && flowNodes.length > 0) {
            const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(flowNodes, flowEdges);
            setNodes(layoutedNodes);
            setEdges(layoutedEdges);
        } else {
            setNodes(flowNodes);
            setEdges(flowEdges);
        }

        setTimeout(() => { reactFlowInstance.fitView({ padding: 0.2 }); }, 100);

    }, [rawNodesData, rawUsers, filterStore, filterDept, isReadOnly, reactFlowInstance]);

    // --- OPÇÕES DE FILTRO ---
    const storeOptions = useMemo(() => [...new Set(rawUsers.map(u => u.loja).filter(Boolean))].map(s => ({ value: s, label: s })), [rawUsers]);
    const deptOptions = useMemo(() => [...new Set(rawUsers.map(u => u.departamento).filter(Boolean))].map(d => ({ value: d, label: d })), [rawUsers]);
    const userOptions = useMemo(() => rawUsers.map(u => ({ value: u.id, label: `${u.name} (${u.funcao || '-'})` })), [rawUsers]);

    // --- HANDLERS COM PROTEÇÃO ---
    
    const handleDeleteNode = async (nodeId) => {
        if (isReadOnly) return; // BLOQUEIO
        if (!window.confirm("Excluir esta posição?")) return;
        try {
            await deleteDoc(doc(db, 'Empresas', adminEmpresaId, 'organizationChartNodes', nodeId));
            const children = rawNodesData.filter(n => n.parentId === nodeId);
            const batch = writeBatch(db);
            children.forEach(child => {
                batch.update(doc(db, 'Empresas', adminEmpresaId, 'organizationChartNodes', child.id), { parentId: null });
            });
            await batch.commit();
            fetchFromFirebase();
        } catch (e) { alert("Erro ao excluir: " + e.message); }
    };

    const handleRemoveUserFromNode = async (nodeId, userId) => {
        if (isReadOnly) return; // BLOQUEIO
        if(!window.confirm("Desvincular usuário da posição?")) return;
        try {
            await updateDoc(doc(db, 'Empresas', adminEmpresaId, 'organizationChartNodes', nodeId), { userId: null });
            fetchFromFirebase();
        } catch(e) { alert("Erro: " + e.message); }
    };

    const handleAssignUser = async () => {
        if (isReadOnly) return; // BLOQUEIO
        if (!userToAssignId || !selectedNodeForAssignment) return;
        try {
            await updateDoc(doc(db, 'Empresas', adminEmpresaId, 'organizationChartNodes', selectedNodeForAssignment), { userId: userToAssignId });
            fetchFromFirebase(); 
            setShowUserAssignmentModal(false);
        } catch (e) { alert("Erro: " + e.message); }
    };

    const handleOpenEditModal = (nodeId) => {
        if (isReadOnly) return; // BLOQUEIO
        const node = nodes.find(n => n.id === nodeId);
        if (node) {
            setEditingNodeData(node);
            setEditNodeName(node.data.label);
            setShowEditNodeModal(true);
        }
    };
    
    const handleSaveEditNode = async () => {
        if (isReadOnly || !editingNodeData) return; // BLOQUEIO
        try {
            await updateDoc(doc(db, 'Empresas', adminEmpresaId, 'organizationChartNodes', editingNodeData.id), { name: editNodeName });
            fetchFromFirebase();
            setShowEditNodeModal(false);
        } catch(e) { alert("Erro: " + e.message); }
    };

    const handleToggleManagerView = async (userId, newValue) => {
        if (isReadOnly || !userId) return; // BLOQUEIO
        try {
            await updateDoc(doc(db, 'users', userId), { canViewSubordinates: newValue });
            setRawUsers(prev => prev.map(u => u.id === userId ? { ...u, canViewSubordinates: newValue } : u));
        } catch (e) { alert("Erro ao alterar permissão: " + e.message); }
    };

    const handleAutoLayout = useCallback(async () => {
        if (isReadOnly || nodes.length === 0) return; // BLOQUEIO
        const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(nodes, edges);
        setNodes([...layoutedNodes]);
        setEdges([...layoutedEdges]);
        
        if (!filterStore && !filterDept) {
            try {
                const batch = writeBatch(db);
                layoutedNodes.forEach(n => {
                    if (n.id) {
                        const ref = doc(db, 'Empresas', adminEmpresaId, 'organizationChartNodes', n.id);
                        batch.update(ref, { position: n.position });
                    }
                });
                await batch.commit();
            } catch (e) { console.error("Erro auto layout:", e); }
        }
        setTimeout(() => reactFlowInstance.fitView({ padding: 0.2 }), 200);
    }, [nodes, edges, adminEmpresaId, setNodes, setEdges, isReadOnly, filterStore, filterDept, reactFlowInstance]);

    const handleSyncWithRegistry = async () => {
        if (isReadOnly) return; // BLOQUEIO
        if (!window.confirm("Isso irá reconstruir TODO o organograma baseado no campo 'Gestor' do cadastro. Posições manuais serão perdidas. Continuar?")) return;
        setLoading(true);
        try {
            const batch = writeBatch(db);
            const nodesRef = collection(db, 'Empresas', adminEmpresaId, 'organizationChartNodes');
            
            rawNodesData.forEach(node => {
                const ref = doc(db, 'Empresas', adminEmpresaId, 'organizationChartNodes', node.id);
                batch.delete(ref);
            });
            
            const emailToNodeIdMap = {};
            const newNodesData = [];

            rawUsers.forEach(user => {
                const newDocRef = doc(nodesRef);
                emailToNodeIdMap[user.email] = newDocRef.id;
                emailToNodeIdMap[user.id] = newDocRef.id; 

                newNodesData.push({
                    docRef: newDocRef,
                    data: {
                        name: user.name,
                        userId: user.id,
                        parentId: null,
                        createdAt: serverTimestamp()
                    },
                    managerEmail: user.managerEmail
                });
            });

            newNodesData.forEach(node => {
                if (node.managerEmail && emailToNodeIdMap[node.managerEmail]) {
                    node.data.parentId = emailToNodeIdMap[node.managerEmail];
                }
            });

            newNodesData.forEach(node => { batch.set(node.docRef, node.data); });
            await batch.commit();

            await fetchFromFirebase();
            
        } catch (err) {
            alert("Erro na sincronização: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    const onConnect = useCallback(async (params) => {
        if (isReadOnly || params.source === params.target) return; // BLOQUEIO
        try {
            const targetRef = doc(db, 'Empresas', adminEmpresaId, 'organizationChartNodes', params.target);
            await updateDoc(targetRef, { parentId: params.source });
            setRawNodesData(prev => prev.map(n => n.id === params.target ? { ...n, parentId: params.source } : n));
        } catch (e) { console.error(e); }
    }, [adminEmpresaId, isReadOnly]);

    const onNodeDragStop = useCallback(async (event, node) => {
        // BLOQUEIO: Se for ReadOnly OU se tiver filtro ativo (pois a posição filtrada é temporária)
        if (isReadOnly || !node.id || filterStore || filterDept) return;
        try {
            await updateDoc(doc(db, 'Empresas', adminEmpresaId, 'organizationChartNodes', node.id), { position: node.position });
        } catch (e) { console.error("Erro drag:", e); }
    }, [adminEmpresaId, isReadOnly, filterStore, filterDept]);

    const handleAddNode = async () => {
        if (isReadOnly || !newNodeName.trim()) return; // BLOQUEIO
        try {
            const collectionRef = collection(db, 'Empresas', adminEmpresaId, 'organizationChartNodes');
            await addDoc(collectionRef, {
                name: newNodeName,
                userId: null,
                parentId: addNodeParentId,
                position: { x: 0, y: 0 },
                createdAt: serverTimestamp()
            });
            fetchFromFirebase();
            setShowAddNodeModal(false);
            setNewNodeName("");
        } catch (e) { alert("Erro ao criar: " + e.message); }
    };

    if (loading && nodes.length === 0) return <div className="organograma-loading">Carregando estrutura...</div>;
    if (error) return <div className="organograma-error">{error}</div>;

    return (
        <div className="organograma-container" style={{ height: 'calc(100vh - 100px)' }}>
            
            <div className="organograma-controls">
                {/* FILTROS (VISÍVEIS PARA TODOS E WHITE-LABELED) */}
                <div style={{display: 'flex', gap: '10px', alignItems: 'center', marginRight: 'auto', flexWrap: 'wrap'}}>
                    <span style={{fontSize: '0.9rem', fontWeight: '600', color: '#555'}}>Filtrar Visualização:</span>
                    <Select 
                        options={storeOptions} 
                        value={filterStore} 
                        onChange={setFilterStore} 
                        isClearable 
                        placeholder="Todas as Lojas" 
                        styles={customSelectStyles} // CSS Variables aplicadas aqui
                    />
                    <Select 
                        options={deptOptions} 
                        value={filterDept} 
                        onChange={setFilterDept} 
                        isClearable 
                        placeholder="Todos Deptos." 
                        styles={customSelectStyles}
                    />
                </div>

                {/* BOTÕES DE AÇÃO (SÓ APARECEM SE TIVER PERMISSÃO) */}
                {!isReadOnly && (
                    <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                        <div className="divider-vertical"></div>
                        <button onClick={() => { setAddNodeParentId(null); setShowAddNodeModal(true); }} className="org-button control primary-action">
                             + Raiz
                        </button>
                        <button onClick={() => { 
                            const sel = nodes.find(n => n.selected);
                            if(sel) { setAddNodeParentId(sel.id); setShowAddNodeModal(true); }
                            else alert("Selecione um pai primeiro.");
                        }} className="org-button control">
                             + Sub.
                        </button>
                        <div className="divider-vertical"></div>
                        <button onClick={handleSyncWithRegistry} className="org-button control warning" title="Resetar e sincronizar com Cadastro">
                            🔄 Sync
                        </button>
                        <button onClick={handleAutoLayout} className="org-button control info" title="Organizar Visual">
                            📐 Auto
                        </button>
                    </div>
                )}
            </div>

            <ReactFlow
                nodes={nodes} edges={edges}
                onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
                onConnect={isReadOnly ? undefined : onConnect}
                onNodeDragStop={isReadOnly ? undefined : onNodeDragStop}
                nodeTypes={nodeTypes}
                fitView
                minZoom={0.05} 
                maxZoom={4}
                className="reactflow-wrapper"
                // Desabilita interações para quem não pode editar
                elementsSelectable={!isReadOnly}
                nodesConnectable={!isReadOnly}
                nodesDraggable={!isReadOnly && !filterStore && !filterDept} 
            >
                <MiniMap />
                <Controls showInteractive={!isReadOnly} />
                <Background color="#f0f2f5" gap={20} />
            </ReactFlow>

            {/* MODAIS (Só renderizam se não for ReadOnly, mas mantemos o check nos handlers também) */}
            {!isReadOnly && (
                <>
                    {showAddNodeModal && (
                        <div className="modal-backdrop">
                            <div className="modal-content-organograma">
                                <h3>Nova Posição</h3>
                                <input className="form-control" placeholder="Nome do Cargo" value={newNodeName} onChange={e => setNewNodeName(e.target.value)} autoFocus />
                                <div className="modal-actions">
                                    <button onClick={() => setShowAddNodeModal(false)} className="btn btn-secondary">Cancelar</button>
                                    <button onClick={handleAddNode} className="btn btn-primary">Criar</button>
                                </div>
                            </div>
                        </div>
                    )}
                    {showUserAssignmentModal && (
                        <div className="modal-backdrop">
                            <div className="modal-content-organograma">
                                <h3>Atribuir Usuário</h3>
                                <Select options={userOptions} onChange={opt => setUserToAssignId(opt?.value)} styles={customSelectStyles} placeholder="Buscar..." autoFocus />
                                <div className="modal-actions">
                                    <button onClick={() => setShowUserAssignmentModal(false)} className="btn btn-secondary">Cancelar</button>
                                    <button onClick={handleAssignUser} className="btn btn-primary">Salvar</button>
                                </div>
                            </div>
                        </div>
                    )}
                    {showEditNodeModal && (
                        <div className="modal-backdrop">
                        <div className="modal-content-organograma">
                            <h3>Editar Posição</h3>
                            <input className="form-control" value={editNodeName} onChange={e => setEditNodeName(e.target.value)} autoFocus />
                            <div className="modal-actions">
                                <button onClick={() => setShowEditNodeModal(false)} className="btn btn-secondary">Cancelar</button>
                                <button onClick={handleSaveEditNode} className="btn btn-primary">Salvar</button>
                            </div>
                        </div>
                    </div>
                    )}
                </>
            )}
        </div>
    );
};

export default function Organograma() {
  return (
    <ReactFlowProvider>
      <OrganogramaContent />
    </ReactFlowProvider>
  );
}